/**
 * AudienceExclusionsPanel — post-send visibility of WHY recipients were excluded
 * from a flight (email opt-out / suppressed / missing email / audience filter).
 *
 * Prefers the breakdown frozen at finalize (metadata.audience_exclusions); for
 * flights sent before that existed, falls back to a live flight-audience-count
 * compute (current list state). Visibility-only — never affects sends.
 */
import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../services/supabase";
import {
  summarizeAudienceStats,
  type AudienceStatsLike,
  type ExclusionSummary,
} from "../../utils/audienceExclusions";

interface FlightLike {
  id: string;
  status?: string | null;
  list_id?: string | null;
  voucher_source_flight_id?: string | null;
  metadata?: Record<string, unknown> | null;
}

function fromMetadata(flight: FlightLike): AudienceStatsLike | null {
  const meta = (flight.metadata as Record<string, unknown> | undefined)?.[
    "audience_exclusions"
  ] as Record<string, unknown> | undefined;
  if (!meta) {
    return null;
  }
  return {
    rawAudienceCount: meta["raw_audience_count"] as number | undefined,
    eligibleCount: meta["eligible_count"] as number | undefined,
    excludedTotal: meta["excluded_total"] as number | undefined,
    reasons: meta["reasons"] as AudienceStatsLike["reasons"],
    examples: meta["examples"] as AudienceStatsLike["examples"],
    examplesOmitted: meta["examples_omitted"] as boolean | undefined,
  };
}

const AudienceExclusionsPanel: React.FC<{ flight: FlightLike }> = ({
  flight,
}) => {
  const persisted = useMemo(() => fromMetadata(flight), [flight]);
  const [live, setLive] = useState<AudienceStatsLike | null>(null);
  const isReminder = Boolean(flight.voucher_source_flight_id);

  useEffect(() => {
    // Only fetch live when nothing was frozen and the flight has a list to count.
    if (persisted || !flight.list_id) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.functions.invoke(
          "flight-audience-count",
          { body: { flight_id: flight.id } },
        );
        if (!cancelled && data?.success && data.stats) {
          setLive(data.stats as AudienceStatsLike);
        }
      } catch {
        /* visibility-only; silently skip */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [persisted, flight.id, flight.list_id]);

  const summary: ExclusionSummary | null = useMemo(
    () => summarizeAudienceStats(persisted ?? live),
    [persisted, live],
  );

  if (!summary || summary.excludedTotal <= 0) {
    // Still surface the reminder dual-filter note even when nothing was excluded.
    if (!isReminder) {
      return null;
    }
  }

  return (
    <div className="px-6 pb-6">
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm">
        <h3 className="font-semibold text-gray-800">Audience exclusions</h3>
        {summary && summary.excludedTotal > 0 ? (
          <>
            <p className="mt-1 text-gray-700">
              <span className="font-medium">
                {summary.raw.toLocaleString()}
              </span>{" "}
              in list;{" "}
              <span className="font-medium">
                {summary.eligible.toLocaleString()}
              </span>{" "}
              eligible recipients sent;{" "}
              <span className="font-medium">
                {summary.excludedTotal.toLocaleString()}
              </span>{" "}
              excluded before send.
            </p>
            {summary.hasBreakdown && (
              <ul className="mt-2 space-y-0.5 text-gray-600">
                {summary.reasons.map((r) => (
                  <li key={r.key}>
                    • {r.count.toLocaleString()} — {r.label}
                  </li>
                ))}
              </ul>
            )}
            {summary.examples && summary.examples.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-primary-600 hover:underline">
                  Show excluded examples
                </summary>
                <ul className="mt-1 space-y-0.5 text-gray-600">
                  {summary.examples.map((e) => (
                    <li key={e.email}>
                      {e.email} — {e.label}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {summary.examplesOmitted && (
              <p className="mt-1 text-xs text-gray-400">
                (large audience — counts only, examples omitted)
              </p>
            )}
          </>
        ) : (
          <p className="mt-1 text-gray-600">
            No recipients were excluded before send.
          </p>
        )}
        {isReminder && (
          <p className="mt-2 text-xs text-gray-500">
            This is a reminder/voucher flight: a contact is sent only if they
            are <strong>email-eligible</strong> <em>and</em> have a{" "}
            <strong>reusable voucher from the source flight</strong>. Someone
            excluded from the source flight (e.g. email opt-out) also has no
            voucher to remind, so they won't appear here.
          </p>
        )}
      </div>
    </div>
  );
};

export default AudienceExclusionsPanel;
